/**
 * Single source of truth for product branding & color tokens.
 *
 * Every value is overridable at build time via `REACT_APP_BRAND_*` env vars.
 * This lets us ship the same codebase to multiple customers and produce a
 * white-labeled UI per build.
 *
 * Example `.env.production` for a client called "Acme":
 *   REACT_APP_BRAND_NAME=Acme Studio
 *   REACT_APP_BRAND_VENDOR=Acme Corp
 *   REACT_APP_BRAND_LOGO_URL=https://cdn.acme.com/logo.svg
 *   REACT_APP_PRIMARY_COLOR=#ff4d6d
 *
 * Note: Create-React-App bakes these into the bundle at `npm run build`.
 * To swap brand at runtime without rebuild, see `BRAND_THEME_URL` below
 * (commented stub — implement when a customer actually asks for it).
 */
import type { ThemeConfig } from 'antd';

const env = process.env;

// ── Brand identity ──────────────────────────────────────────────────────────

export const brand = {
  /** Product name shown in the header / login / browser tab. */
  name: env.REACT_APP_BRAND_NAME || 'CubicOS',
  /** Company behind the product (used in footer & copyright). */
  vendor: env.REACT_APP_BRAND_VENDOR || 'Cubicup',
  /** Short tagline shown in login / marketing surfaces. */
  tagline: env.REACT_APP_BRAND_TAGLINE || 'AI Agent Builder',
  /**
   * Logo. The bundled CubicOS mark (public/brand/cubicos-mark.svg) is the
   * default only for the stock CubicOS build. Fail-closed for white-labeling:
   * a build that overrides the product name but not the logo gets the gradient
   * square with `logoFallbackIcon` (currently `ApiOutlined`), never our mark.
   *
   * `??`, not `||` (unlike the other fields): an explicitly empty
   * REACT_APP_BRAND_LOGO_URL must keep meaning "no logo -> gradient fallback".
   */
  logoUrl:
    env.REACT_APP_BRAND_LOGO_URL ??
    (env.REACT_APP_BRAND_NAME ? '' : `${env.PUBLIC_URL ?? ''}/brand/cubicos-mark.svg`),
  /** Gradient pair used when there's no `logoUrl`. */
  gradientFrom: env.REACT_APP_BRAND_GRADIENT_FROM || '#1e3a8a',
  gradientTo:   env.REACT_APP_BRAND_GRADIENT_TO   || '#3b82f6',
  /** Optional support email / website surfaced in the footer. */
  supportEmail: env.REACT_APP_BRAND_SUPPORT_EMAIL || '',
  website:      env.REACT_APP_BRAND_WEBSITE       || '',
};

// ── Color tokens ────────────────────────────────────────────────────────────

export const colors = {
  primary: env.REACT_APP_PRIMARY_COLOR || '#6366f1',
  /** Soft tint of the primary — used for active-nav backgrounds, accent chips, etc. */
  primarySoft: env.REACT_APP_PRIMARY_SOFT_COLOR || '#ede9fe',
  success: env.REACT_APP_SUCCESS_COLOR || '#16a34a',
  warning: env.REACT_APP_WARNING_COLOR || '#d97706',
  error:   env.REACT_APP_ERROR_COLOR   || '#dc2626',
};

// ── Liquid Glass tokens ──────────────────────────────────────────────────────
// Frosted-glass surfaces (Apple "Liquid Glass" style): translucent panels
// with a blurred backdrop, floating over a soft gradient. Used by the app
// shell (sidebar) and applied globally to `.ant-card` in index.css.

export const glass = {
  surface:       'rgba(255, 255, 255, 0.6)',
  surfaceStrong: 'rgba(255, 255, 255, 0.78)',
  border:        'rgba(255, 255, 255, 0.6)',
  /** Lighter translucent line for separators *inside* a glass surface (nav section rules, etc). */
  divider:       'rgba(255, 255, 255, 0.5)',
  blur:          'blur(24px) saturate(180%)',
  shadow:        '0 8px 32px rgba(76, 29, 149, 0.12), 0 1px 1px rgba(255, 255, 255, 0.5) inset',
};

/**
 * Shared inline style for a floating glass panel (sidebar today; any future
 * floating surface — a header, a popover shell — should spread this instead
 * of re-deriving the same five properties from `glass` by hand.
 */
export const glassPanelStyle = {
  background: glass.surfaceStrong,
  backdropFilter: glass.blur,
  WebkitBackdropFilter: glass.blur,
  border: `1px solid ${glass.border}`,
  borderRadius: 20,
  boxShadow: glass.shadow,
} as const;

/** Separator line between sections inside a glass surface (sidebar, workspace panels). */
export const glassDivider = `1px solid ${glass.divider}`;

/** Header/footer bar inside a glass panel: a non-shrinking flex row with a bottom rule. */
export const glassBarStyle = {
  borderBottom: glassDivider,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
} as const;

/** Small item card inside a glass panel (tool, search hit, mention): same surface/border as `.ant-card`, inset radius. */
export const glassInsetStyle = {
  background: glass.surface,
  border: `1px solid ${glass.border}`,
  borderRadius: 10,
} as const;

/** App-shell background — a soft purple/lavender gradient the glass panels float over. */
export const appBackground =
  env.REACT_APP_BRAND_SHELL_BG ||
  'radial-gradient(1200px 800px at 8% -10%, rgba(167, 139, 250, 0.65) 0%, rgba(167, 139, 250, 0) 60%), ' +
  'radial-gradient(1000px 700px at 110% 15%, rgba(96, 165, 250, 0.55) 0%, rgba(96, 165, 250, 0) 55%), ' +
  'linear-gradient(135deg, #e9e3fd 0%, #dbe3fb 45%, #d6cbfa 100%)';

// ── Ant Design theme ────────────────────────────────────────────────────────
// Antd 5+ uses a token system; this gets passed to <ConfigProvider theme={...}>
// so primary buttons, links, focus rings, etc. follow the brand automatically.

export const antdTheme: ThemeConfig = {
  token: {
    colorPrimary:     colors.primary,
    colorSuccess:     colors.success,
    colorWarning:     colors.warning,
    colorError:       colors.error,
    borderRadius:     14,
    fontFamily:       'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    colorTextBase:    '#111111',
    colorBgContainer: '#ffffff',
    colorBgLayout:    'transparent',
    colorBorder:      '#e5e7eb',
    colorBorderSecondary: '#f0f0f0',
    colorTextSecondary:   '#6b7280',
    colorTextTertiary:    '#9ca3af',
    colorFillSecondary:   '#f3f4f6',
    boxShadow:        '0 8px 32px rgba(76,29,149,0.10), 0 1px 2px rgba(0,0,0,0.04)',
    boxShadowSecondary: '0 4px 20px rgba(76,29,149,0.08)',
  },
  components: {
    Card: {
      colorBgContainer: glass.surface,
      borderRadiusLG: 20,
      boxShadow: glass.shadow,
    },
    Button: {
      borderRadius: 12,
    },
    Input: {
      borderRadius: 10,
    },
    Modal: {
      borderRadiusLG: 20,
    },
  },
};

// ── Document title (so the browser tab matches the brand) ──────────────────

if (typeof document !== 'undefined' && brand.name) {
  document.title = brand.name;
}
